import { DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { Button } from '../ui';
import { saveSession } from '../../services/authentication';
import { Session } from '../../services/models/session';
import { launchPopupWindow } from '../../services/popups';
import { createBodyFromMessage, createMailtoLink } from '../../utils/createMailtoLinks';
import { Modal } from '../ui';

interface TwitterLoginBtnProps {
  label?: string;
  checkIfEnable: () => boolean;
  callback?: () => void;
  onSuccess?: () => void;
  disabled?: boolean;
}

const TwitterLoginBtn: FunctionComponent<TwitterLoginBtnProps> = ({
  label,
  checkIfEnable,
  callback,
  onSuccess,
  disabled,
}) => {
  const [disabledModalShown, setDisabledModalShown] = useState(false);

  const mailtoLink = createMailtoLink(
    'help@newslit.co',
    `Need Help with Newslit Twitter Login`,
    createBodyFromMessage('', true)
  );

  const twitterLoginListener = (event: any) => {
    if (event.origin !== window.location.origin) {
      console.log('message event with diff origin');
      return;
    }

    try {
      let session: Session = JSON.parse(event.data);

      if (session && session.account && session.token) {
        saveSession(session);

        onSuccess ? onSuccess() : console.warn('[Newslit] no Twitter Login onSuccess defined');
      }

      window.removeEventListener('message', twitterLoginListener);
    } catch (e) {
      console.log(e);
    }
  };

  const onTwitterLoginClick = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (checkIfEnable && callback && !checkIfEnable()) {
      callback();
      return;
    }

    window.addEventListener('message', twitterLoginListener, false);

    launchPopupWindow(
      window.location.origin + `/popup/twitter`,
      'location=yes,scrollbars=yes,status=yes',
      {
        height: 570,
        width: 620,
      },
      () => {}
    );
  };

  return (
    <>
      {disabled && (
        <div className="rounded-md bg-yellow-50 p-4 mb-4">
          <div className="flex">
            <div className="shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-1">
                  Our Twitter integration is currently disabled. We are actively trying to resolve
                  this and will share updates as additional information becomes available.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The Twitter login link is fetched inside the popup window (its own
          app instance), so this button never observes a fetching state. */}
      <Button
        className="w-full flex twitter-btn"
        variant="twitter"
        onClick={(e: React.SyntheticEvent) =>
          disabled ? setDisabledModalShown(true) : onTwitterLoginClick(e)
        }
      >
        <i className="fa fa-twitter"></i> {!!label ? label : 'Sign in with Twitter'}
      </Button>

      {disabledModalShown && (
        <Modal isOpen={disabledModalShown} onClose={() => setDisabledModalShown(false)}>
          <div className="sm:flex sm:items-start">
            <div className="mt-3 sm:mt-0 sm:ml-4 sm:text-left">
              <DialogTitle
                as="h3"
                className="text-center text-lg leading-6 font-medium text-primary"
              >
                Looking to Sign in with Twitter?
              </DialogTitle>
              <div className="mt-2">
                <p className="">Twitter authentication is not currently working on Newslit.</p>
                <p className="mt-3">
                  If you need to login to your account set up a password to your account. You can
                  also{' '}
                  <a
                    href={mailtoLink}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-secondary hover:text-secondary-dark"
                  >
                    Contact us at help@newslit.co
                  </a>{' '}
                  if you have any questions or need further help.
                </p>
              </div>
              <div className="mt-3">
                <a
                  href="/users/password_reset"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50"
                >
                  Request a Password Reset
                </a>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TwitterLoginBtn;
